import React, {FocusEventHandler, useEffect, useState} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import classNames from 'classnames';
import {GroupTypeBase, InputProps, OptionProps, components} from 'react-select';

import {getDeepValue} from 'helpers/common';
import {translate} from 'helpers/localize';
import Button from 'components/ui/Button';
import ErrorFeedback from '../ErrorFeedback/ErrorFeedback';

import styles from './VatRateAutoComplete.module.scss';
import {IExpenseVat} from 'api/expenses';
import NumberFormat from 'react-number-format';

export interface IProps {
    options: IExpenseVat[];
    name: string;
    onChange: (vat: IExpenseVat) => void;
    disabled?: boolean;
}

const VatRateAutoComplete = ({options, name, onChange, disabled}: IProps) => {
    const [inputValue, setInputValue] = useState<string>('');
    const {control, errors, watch} = useFormContext();
    const value = watch(name);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    return (
        <div className={styles.container}>
            <label htmlFor={name} className={styles.label}>
                VAT rate
            </label>
            <Controller
                control={control}
                name={name}
                render={(field, state) => {
                    return (
                        <div data-notranslate>
                            <CreatableSelect
                                {...field}
                                inputValue={inputValue}
                                defaultInputValue={value}
                                options={options}
                                components={{
                                    NoOptionsMessage,
                                    Option,
                                    Input,
                                    DropdownIndicator: () => null,
                                    IndicatorSeparator: () => null,
                                }}
                                controlShouldRenderValue={false}
                                className={classNames(styles.input, {
                                    [styles.invalid]: getDeepValue(
                                        errors,
                                        name,
                                    ),
                                })}
                                // value={field.value?.value}
                                onChange={(value, action) => {
                                    const newValue = value.value;
                                    setInputValue(newValue);
                                    onChange(value as IExpenseVat);
                                    control.setValue(name, newValue, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }}
                                getOptionLabel={option => option.name}
                                getOptionValue={option => option.value}
                                getNewOptionData={(inputValue, _) => {
                                    return {
                                        name: inputValue,
                                        value: inputValue,
                                        __isNew__: true,
                                    };
                                }}
                                onInputChange={(value, action) => {
                                    // only set the input when the action that caused the
                                    // change equals to "input-change" and ignore the other
                                    // ones like: "set-value", "input-blur", and "menu-close"
                                    if (
                                        action.action === 'input-blur' ||
                                        action.action === 'menu-close' ||
                                        action.action === 'set-value'
                                    ) {
                                        return;
                                    }
                                    if (action.action === 'input-change') {
                                        setInputValue(value);
                                    }
                                }}
                                classNamePrefix="amnis-new"
                                placeholder=""
                                isDisabled={disabled}
                            />
                        </div>
                    );
                }}
            />
            {getDeepValue(errors, name)?.message ? (
                <ErrorFeedback message={getDeepValue(errors, name).message} />
            ) : null}
        </div>
    );
};

const NoOptionsMessage = (props: any) => {
    return (
        <components.NoOptionsMessage {...props}>
            <span>{translate('No options')}</span>
        </components.NoOptionsMessage>
    );
};

const Option: React.ComponentType<
    OptionProps<any, boolean, GroupTypeBase<any>>
> = props => {
    const {data, getStyles, innerProps} = props;

    return (
        <div style={getStyles('option', props)} {...innerProps}>
            {data.__isNew__ ? (
                <Button icon="plus" variant="text">
                    {translate('Use %{name}%', {
                        name: data.name,
                    })}
                </Button>
            ) : (
                <span data-notranslate>
                    {data.name} - {data.value}%
                </span>
            )}
        </div>
    );
};
const Input: React.ComponentType<InputProps> = props => {
    return (
        <>
            <NumberFormat
                {...props}
                className={classNames(styles.numberInput)}
                decimalScale={2}
                isNumericString
            />
            <div className={styles.endAdornment}>%</div>
        </>
    );
};

export default VatRateAutoComplete;
